import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"

import * as React from "react"

import { ServerContextValue, ServerProviderProps } from "./ServerContext.types"
import { ClientLogger } from "@mimber/schema"
import createServer from "./server"
import serverConfig from "./serverConfig"
import { normalizeDomain } from "./ServerContext.utils"

export const ServerContext = React.createContext<
  ServerContextValue | undefined
>(undefined)

const FIVE_MINUTES_MS = 5 * 60 * 1000

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: FIVE_MINUTES_MS,
      retry: 1,
    },
  },
})

export let PCLogger: ClientLogger = {
  debug: console.debug,
  info: console.info,
  warn: console.warn,
  error: console.error,
}

export const ServerProvider: React.FunctionComponent<ServerProviderProps> = ({
  appAgent = "Achievement",
  children,
  debug = false,
  deviceId,
  environment = "development",
  customServerConfig,
  getAccessToken = () => Promise.resolve("portal-token"),
  getOrganizationId,
  logger,
  platform,
  queryClientOptions,
  version = 1,
}) => {
  const contextValue = React.useMemo(() => {
    const activeServerConfig = customServerConfig ?? serverConfig[environment]

    const normalizedDomain = normalizeDomain(
      activeServerConfig.domain,
      platform
    )

    const server = createServer({
      appAgent,
      axiosRequestConfig: {
        baseURL: `${normalizedDomain}api/v${version}/`,
        withCredentials: true,
        headers: deviceId
          ? {
              "device-id": deviceId,
            }
          : undefined,
      },
      debug,
      getAuthorization: async () => `Bearer ${await getAccessToken()}`,
      getOrganizationId,
    })

    const unauthenticatedServer = createServer({
      appAgent,
      axiosRequestConfig: {
        baseURL: `${normalizedDomain}api/v${version}/`,
      },
      debug,
    })

    return { server, unauthenticatedServer }
  }, [
    customServerConfig,
    environment,
    appAgent,
    version,
    deviceId,
    debug,
    getAccessToken,
    getOrganizationId,
    platform,
  ])

  React.useEffect(() => {
    if (logger) {
      PCLogger = logger
    }
  }, [logger])

  React.useEffect(() => {
    if (queryClientOptions) {
      queryClient.setDefaultOptions(queryClientOptions)
    }
  }, [queryClientOptions])

  return (
    <QueryClientProvider client={queryClient}>
      <ServerContext.Provider value={contextValue}>
        {children}
        {typeof document !== "undefined" && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </ServerContext.Provider>
    </QueryClientProvider>
  )
}

export const useServerContext = () => {
  const context = React.useContext(ServerContext)

  if (!context) {
    throw new Error("useServerContext must be used within ServerProvider")
  }

  return context
}
